import ApprovalStatus from "./enums/ApprovalStatus.js";
import HistoryOperation from "./enums/HistoryOperation";
import {EntityRelation} from "./types/EntityRelation";
import {PermitEntityResult} from "./types/PermitEntityResult.js";

function lastOfType<T>(history: PermitEntityResult<T>[], status: ApprovalStatus): PermitEntityResult<T> | undefined {
	if (history.length === 0) {
		return;
	}

	return ofType(history, status).at(-1);
}

function ofType<T>(history: PermitEntityResult<T>[], status: ApprovalStatus): PermitEntityResult<T>[] {
	return history.filter((x) => x.status === status);
}

function lastApproved<T>(history: PermitEntityResult<T>[]): PermitEntityResult<T> | undefined {
	return lastOfType(history, ApprovalStatus.Approved);
}

function lastSubmitted<T>(history: PermitEntityResult<T>[]): PermitEntityResult<T> | undefined {
	return lastOfType(history, ApprovalStatus.Submitted);
}

function getApproved<T>(history: PermitEntityResult<T>[]): PermitEntityResult<T>[] {
	return ofType(history, ApprovalStatus.Approved);
}

function getSubmitted<T>(history: PermitEntityResult<T>[]): PermitEntityResult<T>[] {
	return ofType(history, ApprovalStatus.Submitted);
}

class EntityHistory {
	static allChanged<T>(history: PermitEntityResult<T>[]): EntityRelation<T>[] {
		const map = new Map<T, EntityRelation<T>>();

		for (const a of getApproved(history)) {
			map.set(a.id, {original: a, changed: undefined});
		}

		for (const s of getSubmitted(history)) {
			if (s.operation === HistoryOperation.Replace) {
				map.get(history[s.replaces].id)!.changed = s;

			} else {
				map.set(s.id, {original: s, changed: undefined});
			}
		}

		return Array.from(map.values());
	}

	static approvedOrDefault<T>(history: PermitEntityResult<T>[]): PermitEntityResult<T> | undefined {
		return lastApproved(history) ?? lastSubmitted(history);
	}

	static changed<T>(history: PermitEntityResult<T>[]): EntityRelation<T> | undefined {
		if (history.length === 0) {
			return;
		}

		const la = lastApproved(history);
		const ls = lastSubmitted(history);
		if (!la && !ls) {
			return;
		}

		return {original: la ?? ls!, changed: la == null ? undefined : ls};
	}
}

export {EntityHistory};
